import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export type MediaRow = {
  type: "image" | "audio" | "video";
  url: string;
  title?: string;
};

/**
 * Images, audio and video shown on a profile, pasted as URLs.
 *
 * Shared by the profile page and the registration form's profile step: the
 * form writes straight through to the profile, so anything the form asks
 * differently is a difference the member has to reconcile.
 */
export function MediaEditor({
  value,
  onChange,
}: {
  value: MediaRow[];
  onChange: (next: MediaRow[]) => void;
}) {
  return (
    <div className="space-y-3">
      {value.map((item, i) => (
        <div key={i} className="flex gap-2">
          <Select
            value={item.type}
            onValueChange={(next) =>
              onChange(
                value.map((x, j) =>
                  j === i ? { ...x, type: next as MediaRow["type"] } : x
                )
              )
            }
          >
            <SelectTrigger className="w-28" aria-label="Media type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="image">Image</SelectItem>
              <SelectItem value="audio">Audio</SelectItem>
              <SelectItem value="video">Video</SelectItem>
            </SelectContent>
          </Select>
          <Input
            aria-label="Media address"
            placeholder="https://…"
            className="flex-[2]"
            value={item.url}
            onChange={(e) =>
              onChange(value.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))
            }
          />
          <Input
            aria-label="Caption"
            placeholder="Caption"
            className="flex-1"
            value={item.title ?? ""}
            onChange={(e) =>
              onChange(value.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))
            }
          />
          <Button
            variant="ghost"
            size="icon"
            aria-label="Remove media"
            onClick={() => onChange(value.filter((_, j) => j !== i))}
          >
            <Trash2 />
          </Button>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onChange([...value, { type: "image", url: "" }])}
      >
        <Plus data-slot="icon" /> Add media
      </Button>
    </div>
  );
}

export default MediaEditor;
